import { useState, useEffect, useRef, useMemo } from 'react';

/**
 * useProgressiveList
 *
 * Renders only `batchSize` items at first, and reveals another batch each
 * time the user scrolls close to the end of the currently-rendered list.
 *
 * Why this instead of a full virtualization library (react-window etc.):
 * this codebase's card grids rely on plain CSS grid layout, nth-child
 * entrance animations, and hover-popup positioning computed straight off
 * the real DOM node — none of that has to change. Cards already rendered
 * stay mounted (so hover/animation behavior is untouched); we simply delay
 * mounting the ones further down the list until they're about to be needed.
 *
 * This also matters beyond DOM/image count: some grids (e.g. SeriesPage's
 * SeriesBand) fire their own API call on mount. Progressively revealing
 * items means those calls only fire for series the user has actually
 * scrolled to, instead of every series in the catalog firing at once.
 *
 * Usage:
 *   const { visibleItems, sentinelRef } = useProgressiveList(allMovies, 24);
 *   return <>
 *     <div className="grid">{visibleItems.map(renderCard)}</div>
 *     <div ref={sentinelRef} />
 *   </>;
 */
export function useProgressiveList(items, batchSize = 24) {
  const safeItems = useMemo(() => items || [], [items]);
  const [count, setCount] = useState(batchSize);
  const sentinelRef = useRef(null);

  // Reset the visible count whenever the underlying list identity changes
  // (e.g. switching genre filter, or a fresh search) so we don't carry
  // over a stale "revealed so far" count from a previous list.
  useEffect(() => { setCount(batchSize); }, [safeItems, batchSize]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    if (count >= safeItems.length) return undefined; // nothing more to reveal

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setCount((c) => Math.min(c + batchSize, safeItems.length));
        }
      },
      { rootMargin: '600px 0px' } // start loading the next batch well before it's on-screen
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [count, safeItems.length, batchSize]);

  const visibleItems = useMemo(
    () => safeItems.slice(0, count),
    [safeItems, count]
  );

  return { visibleItems, sentinelRef, hasMore: count < safeItems.length };
}
